import * as THREE from 'three';

export class ParticleSystem {
  private scene: THREE.Scene;
  private particles: Particle[] = [];

  constructor(scene: THREE.Scene) {
    this.scene = scene;
  }

  // 创建爆炸效果
  createExplosion(position: THREE.Vector3, scale: number = 1): void {
    const particleCount = 30 * scale;

    for (let i = 0; i < particleCount; i++) {
      const particle = new ExplosionParticle(position, scale);
      this.particles.push(particle);
      this.scene.add(particle.mesh);
    }
  }

  // 创建烟雾轨迹
  createSmokeTrail(position: THREE.Vector3): void {
    const particle = new SmokeParticle(position);
    this.particles.push(particle);
    this.scene.add(particle.mesh);
  }

  // 创建炮口火光
  createMuzzleFlash(position: THREE.Vector3, direction: THREE.Vector3): void {
    const flash = new MuzzleFlash(position, direction);
    this.particles.push(flash);
    this.scene.add(flash.mesh);
  }

  // 创建道具收集特效
  createPowerupEffect(position: THREE.Vector3, color: number): void {
    for (let i = 0; i < 20; i++) {
      const particle = new PowerupParticle(position, color);
      this.particles.push(particle);
      this.scene.add(particle.mesh);
    }
  }

  // 创建护盾特效
  createShieldEffect(position: THREE.Vector3): ShieldEffect {
    const shield = new ShieldEffect(position);
    this.scene.add(shield.mesh);
    return shield;
  }

  update(deltaTime: number): void {
    for (let i = this.particles.length - 1; i >= 0; i--) {
      const particle = this.particles[i];
      particle.update(deltaTime);

      if (!particle.isAlive) {
        this.scene.remove(particle.mesh);
        particle.mesh.geometry.dispose();
        (particle.mesh.material as THREE.Material).dispose();
        this.particles.splice(i, 1);
      }
    }
  }

  clear(): void {
    this.particles.forEach(particle => {
      this.scene.remove(particle.mesh);
      particle.mesh.geometry.dispose();
      (particle.mesh.material as THREE.Material).dispose();
    });
    this.particles = [];
  }
}

// 粒子基类
abstract class Particle {
  public mesh: THREE.Mesh;
  public isAlive: boolean = true;
  protected lifetime: number;
  protected age: number = 0;
  protected velocity: THREE.Vector3;

  constructor() {
    this.mesh = new THREE.Mesh();
    this.velocity = new THREE.Vector3();
    this.lifetime = 1;
  }

  abstract update(deltaTime: number): void;
}

// 爆炸粒子
class ExplosionParticle extends Particle {
  constructor(position: THREE.Vector3, scale: number) {
    super();

    const size = 0.1 + Math.random() * 0.2 * scale;
    const geometry = new THREE.SphereGeometry(size, 4, 4);
    const color = new THREE.Color().setHSL(Math.random() * 0.1, 1, 0.5);
    const material = new THREE.MeshBasicMaterial({ color: color });

    this.mesh = new THREE.Mesh(geometry, material);
    this.mesh.position.copy(position);

    // 随机速度
    this.velocity = new THREE.Vector3(
      (Math.random() - 0.5) * 10,
      Math.random() * 8,
      (Math.random() - 0.5) * 10
    );

    this.lifetime = 0.5 + Math.random() * 0.3;
  }

  update(deltaTime: number): void {
    this.age += deltaTime;

    if (this.age >= this.lifetime) {
      this.isAlive = false;
      return;
    }

    // 更新位置
    this.mesh.position.add(this.velocity.clone().multiplyScalar(deltaTime));

    // 重力
    this.velocity.y -= 20 * deltaTime;

    // 缩放渐变
    const progress = this.age / this.lifetime;
    const scale = 1 - progress;
    this.mesh.scale.setScalar(scale);

    // 颜色渐变
    const material = this.mesh.material as THREE.MeshBasicMaterial;
    material.opacity = 1 - progress;
    material.transparent = true;
  }
}

// 烟雾粒子
class SmokeParticle extends Particle {
  constructor(position: THREE.Vector3) {
    super();

    const size = 0.5;
    const geometry = new THREE.SphereGeometry(size, 8, 8);
    const material = new THREE.MeshBasicMaterial({
      color: 0x666666,
      transparent: true,
      opacity: 0.5
    });

    this.mesh = new THREE.Mesh(geometry, material);
    this.mesh.position.copy(position);

    this.velocity = new THREE.Vector3(
      (Math.random() - 0.5) * 2,
      1 + Math.random(),
      (Math.random() - 0.5) * 2
    );

    this.lifetime = 1 + Math.random() * 0.5;
  }

  update(deltaTime: number): void {
    this.age += deltaTime;

    if (this.age >= this.lifetime) {
      this.isAlive = false;
      return;
    }

    // 更新位置
    this.mesh.position.add(this.velocity.clone().multiplyScalar(deltaTime));

    // 减速
    this.velocity.multiplyScalar(0.98);

    // 扩散和淡出
    const progress = this.age / this.lifetime;
    const scale = 1 + progress * 2;
    this.mesh.scale.setScalar(scale);

    const material = this.mesh.material as THREE.MeshBasicMaterial;
    material.opacity = 0.5 * (1 - progress);
  }
}

// 炮口火光
class MuzzleFlash extends Particle {
  constructor(position: THREE.Vector3, direction: THREE.Vector3) {
    super();

    const geometry = new THREE.SphereGeometry(0.3, 8, 8);
    const material = new THREE.MeshBasicMaterial({
      color: 0xffff00,
      transparent: true,
      opacity: 1
    });

    this.mesh = new THREE.Mesh(geometry, material);
    this.mesh.position.copy(position).add(direction.clone().multiplyScalar(0.5));

    this.lifetime = 0.1;
  }

  update(deltaTime: number): void {
    this.age += deltaTime;

    if (this.age >= this.lifetime) {
      this.isAlive = false;
      return;
    }

    const progress = this.age / this.lifetime;
    const material = this.mesh.material as THREE.MeshBasicMaterial;
    material.opacity = 1 - progress;
  }
}

// 道具收集粒子
class PowerupParticle extends Particle {
  constructor(position: THREE.Vector3, color: number) {
    super();

    const size = 0.1;
    const geometry = new THREE.SphereGeometry(size, 4, 4);
    const material = new THREE.MeshBasicMaterial({ color: color });

    this.mesh = new THREE.Mesh(geometry, material);
    this.mesh.position.copy(position);

    const angle = Math.random() * Math.PI * 2;
    const speed = 3 + Math.random() * 2;
    this.velocity = new THREE.Vector3(
      Math.cos(angle) * speed,
      Math.random() * 5,
      Math.sin(angle) * speed
    );

    this.lifetime = 0.8;
  }

  update(deltaTime: number): void {
    this.age += deltaTime;

    if (this.age >= this.lifetime) {
      this.isAlive = false;
      return;
    }

    this.mesh.position.add(this.velocity.clone().multiplyScalar(deltaTime));
    this.velocity.y -= 15 * deltaTime;

    const progress = this.age / this.lifetime;
    const scale = 1 - progress;
    this.mesh.scale.setScalar(scale);

    const material = this.mesh.material as THREE.MeshBasicMaterial;
    material.opacity = 1 - progress;
    material.transparent = true;
  }
}

// 护盾特效
export class ShieldEffect {
  public mesh: THREE.Mesh;
  private pulseSpeed: number = 3;
  private offset: number = 0;

  constructor(position: THREE.Vector3) {
    const geometry = new THREE.SphereGeometry(2, 16, 16);
    const material = new THREE.MeshBasicMaterial({
      color: 0x00ffff,
      transparent: true,
      opacity: 0.3,
      wireframe: true
    });

    this.mesh = new THREE.Mesh(geometry, material);
    this.mesh.position.copy(position);
  }

  update(deltaTime: number, position: THREE.Vector3): void {
    this.offset += this.pulseSpeed * deltaTime;
    this.mesh.position.copy(position);
    this.mesh.position.y += 1;

    // 脉冲效果
    const pulse = 0.3 + Math.sin(this.offset) * 0.1;
    const material = this.mesh.material as THREE.MeshBasicMaterial;
    material.opacity = pulse;

    // 旋转
    this.mesh.rotation.y += deltaTime;
  }

  remove(scene: THREE.Scene): void {
    scene.remove(this.mesh);
    this.mesh.geometry.dispose();
    (this.mesh.material as THREE.Material).dispose();
  }
}
